import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Wrench } from "lucide-react";
import { useState } from "react";

export interface WorkDetails {
  name?: string;           // Vainajan nimi
  birth_date?: string;     // Syntymäaika (teksti)
  death_date?: string;     // Kuolinaika (teksti)
  font?: string;           // Fontti
  engraving_style?: string; // Kaiverrustyyli
  text_size?: string;      // Tekstin koko
  additional_text?: string; // Lisäteksti (runo, motto)
  ornament?: string;       // Koristekuvio
  stone_description?: string; // Kiven kuvaus (väri, koko, sijainti)
  notes?: string;          // Vapaat muistiinpanot
}

interface WorkDetailsFormProps {
  value: WorkDetails | null;
  onChange: (value: WorkDetails | null) => void;
  disabled?: boolean;
}

const FONTS = [
  "Times New Roman",
  "Arial",
  "Helvetica",
  "Garamond",
  "Palatino",
  "Book Antiqua",
  "Georgia",
  "Muu"
];

const ENGRAVING_STYLES = [
  "Kullattu",
  "Hopeinen",
  "Maalattu musta",
  "Maalattu valkoinen",
  "Maalattu ruskea",
  "Syvennetty",
  "Kohotettu",
  "Muu"
];

const ORNAMENTS = [
  "Ei koristetta",
  "Risti",
  "Sydän",
  "Ruusu",
  "Enkeli",
  "Kyyhky",
  "Kruunu",
  "Kynttilä",
  "Auringonnousu",
  "Muu"
];

export function WorkDetailsForm({ value, onChange, disabled }: WorkDetailsFormProps) {
  const [isOpen, setIsOpen] = useState(!!value);
  
  const handleChange = (field: keyof WorkDetails, newValue: string) => {
    const updated = {
      ...(value || {}),
      [field]: newValue || undefined
    };
    
    // If all fields are empty, set to null
    const hasValue = Object.values(updated).some(v => v);
    onChange(hasValue ? updated : null);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border rounded-lg">
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="w-full justify-start gap-2 p-3 h-auto"
          disabled={disabled}
        >
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Wrench className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Työn tiedot</span>
          {value && Object.values(value).some(v => v) && (
            <span className="ml-2 text-xs text-muted-foreground">
              (täytetty)
            </span>
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Vainajan nimi */}
          <div className="space-y-2">
            <Label>Vainajan nimi</Label>
            <Input
              placeholder="Esim. Meikäläinen Matti"
              value={value?.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              disabled={disabled}
            />
          </div>

          {/* Fontti */}
          <div className="space-y-2">
            <Label>Fontti</Label>
            <Select
              value={value?.font || ""}
              onValueChange={(v) => handleChange("font", v)}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Valitse fontti" />
              </SelectTrigger>
              <SelectContent>
                {FONTS.map((font) => (
                  <SelectItem key={font} value={font}>
                    {font}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Syntymäaika */}
          <div className="space-y-2">
            <Label>Syntymäaika</Label>
            <Input
              placeholder="Esim. 1.1.1940"
              value={value?.birth_date || ""}
              onChange={(e) => handleChange("birth_date", e.target.value)}
              disabled={disabled}
            />
          </div>

          {/* Kuolinaika */}
          <div className="space-y-2">
            <Label>Kuolinaika</Label>
            <Input
              placeholder="Esim. 15.12.2024"
              value={value?.death_date || ""}
              onChange={(e) => handleChange("death_date", e.target.value)}
              disabled={disabled}
            />
          </div>

          {/* Tekstin koko */}
          <div className="space-y-2">
            <Label>Tekstin koko</Label>
            <Input
              placeholder="Esim. 3 cm"
              value={value?.text_size || ""}
              onChange={(e) => handleChange("text_size", e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Kaiverrustyyli */}
          <div className="space-y-2">
            <Label>Kaiverrustyyli</Label>
            <Select
              value={value?.engraving_style || ""}
              onValueChange={(v) => handleChange("engraving_style", v)}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Valitse tyyli" />
              </SelectTrigger>
              <SelectContent>
                {ENGRAVING_STYLES.map((style) => (
                  <SelectItem key={style} value={style}>
                    {style}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Koristekuvio */}
          <div className="space-y-2">
            <Label>Koristekuvio</Label>
            <Select
              value={value?.ornament || ""}
              onValueChange={(v) => handleChange("ornament", v)}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Valitse koriste" />
              </SelectTrigger>
              <SelectContent>
                {ORNAMENTS.map((ornament) => (
                  <SelectItem key={ornament} value={ornament}>
                    {ornament}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Lisäteksti */}
        <div className="space-y-2">
          <Label>Lisäteksti (runo, motto)</Label>
          <Textarea
            placeholder="Esim. Rakas isä ja isoisä"
            value={value?.additional_text || ""}
            onChange={(e) => handleChange("additional_text", e.target.value)}
            disabled={disabled}
            rows={2}
          />
        </div>

        {/* Kiven kuvaus */}
        <div className="space-y-2">
          <Label>Kiven kuvaus</Label>
          <Textarea
            placeholder="Esim. Tumma harmaa graniitti, n. 80x60 cm, vasemmalla puolella hautaa"
            value={value?.stone_description || ""}
            onChange={(e) => handleChange("stone_description", e.target.value)}
            disabled={disabled}
            rows={2}
          />
        </div>

        {/* Muistiinpanot */}
        <div className="space-y-2">
          <Label>Muistiinpanot</Label>
          <Textarea
            placeholder="Muita huomioita työstä..."
            value={value?.notes || ""}
            onChange={(e) => handleChange("notes", e.target.value)}
            disabled={disabled}
            rows={2}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
